import { useState, useCallback, useEffect, useRef } from 'react';
import MobileOKService, {
  MobileOKRequestParams,
  MobileOKResult,
} from '../libs/MobileOKService';
import axiosClient from '../libs/axiosClient';

// 글로벌 콜백 함수를 위한 Window 확장
declare global {
  interface Window {
    mobileOKCallback?: (response: string) => void;
  }
}

interface UseMobileOKOptions {
  // 사용자 ID (로그인 상태일 경우)
  userId?: number;
  // 인증 성공 콜백
  onSuccess?: (result: MobileOKResult) => void;
  // 인증 실패 콜백
  onError?: (error: Error) => void;
  // 인증 취소 콜백
  onCancel?: () => void;
}

interface UseMobileOKReturn {
  // 본인확인 시작
  startVerification: (params: MobileOKRequestParams) => Promise<void>;
  // 로딩 상태
  isLoading: boolean;
  // 에러 상태
  error: Error | null;
  // SDK 로드 상태
  isSDKLoaded: boolean;
}

/**
 * Mobile-OK 본인확인 커스텀 훅
 *
 * @example
 * const { startVerification, isLoading } = useMobileOK({
 *   onSuccess: (result) => console.log('인증 성공:', result),
 *   onError: (error) => console.error('인증 실패:', error),
 * });
 *
 * // 본인확인 시작
 * await startVerification({
 *   usageCode: '01001', // 회원가입
 *   returnUrl: `${window.location.origin}/auth/mobileok/callback`,
 * });
 */
export function useMobileOK(options?: UseMobileOKOptions): UseMobileOKReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isSDKLoaded, setIsSDKLoaded] = useState(false);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // SDK 사전 로드 및 글로벌 콜백 함수 등록
  useEffect(() => {
    MobileOKService.loadScript()
      .then(() => setIsSDKLoaded(true))
      .catch((err) => {
        console.error('[useMobileOK] SDK load failed:', err);
        setIsSDKLoaded(false);
      });

    // 글로벌 콜백 함수 등록
    window.mobileOKCallback = (response: string) => {
      console.log('[MobileOK] Callback received:', response);
      try {
        const data = JSON.parse(response);
        console.log('[MobileOK] Parsed callback data:', data);

        // 성공 코드: 2000, 0000, 0
        const successCodes = ['2000', '0000', '0'];
        if (successCodes.includes(String(data.resultCode))) {
          optionsRef.current?.onSuccess?.(data);
        } else {
          const error = new Error(data.resultMsg || '본인확인에 실패했습니다.');
          optionsRef.current?.onError?.(error);
        }
      } catch (err) {
        console.error('[MobileOK] Callback parse error:', err);
        optionsRef.current?.onError?.(err instanceof Error ? err : new Error('콜백 처리 실패'));
      }
    };

    return () => {
      // 컴포넌트 언마운트 시 콜백 제거
      delete window.mobileOKCallback;
    };
  }, []);

  // 본인확인 시작
  const startVerification = useCallback(
    async (params: MobileOKRequestParams) => {
      console.log('[MobileOK] startVerification called with:', params);
      setIsLoading(true);
      setError(null);

      try {
        // MobileOKService.startAuth() 사용
        // - 환경변수 기반 API_BASE URL 사용
        // - access_token 쿼리 파라미터 자동 추가
        // - clientTxId 생성 및 sessionStorage 저장
        await MobileOKService.startAuth({
          usageCode: params.usageCode,
          serviceType: params.serviceType,
          userId: params.userId,
        });

        // 팝업이 열리면 로딩 상태 해제
        // 실제 결과는 콜백에서 처리
        setIsLoading(false);
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('본인확인 요청 실패');
        setError(error);
        setIsLoading(false);
        options?.onError?.(error);
        throw error;
      }
    },
    [options],
  );

  return {
    startVerification,
    isLoading,
    error,
    isSDKLoaded,
  };
}

/**
 * 본인확인 결과 처리 훅 (콜백 페이지용)
 *
 * @example
 * // /auth/mobileok/callback 페이지에서 사용
 * const { result, isLoading, error } = useMobileOKCallback({
 *   onSuccess: (result) => navigate('/join/complete'),
 *   onError: (error) => navigate('/join?error=verification'),
 * });
 */
export function useMobileOKCallback(options?: UseMobileOKOptions) {
  const [result, setResult] = useState<MobileOKResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const processCallback = async () => {
      try {
        // URL에서 MOKToken 추출
        const urlParams = new URLSearchParams(window.location.search);
        const mokToken = urlParams.get('mokToken');
        const resultCode = urlParams.get('resultCode');

        // 에러 체크 (Mobile-OK 성공 코드: 2000)
        if (resultCode && resultCode !== '2000') {
          const errorMsg =
            urlParams.get('resultMsg') || '본인확인에 실패했습니다.';
          throw new Error(errorMsg);
        }

        if (!mokToken) {
          throw new Error('인증 토큰이 없습니다.');
        }

        // 세션에서 거래 ID 확인 (CSRF 방지)
        const storedTxId = sessionStorage.getItem('mobileok_txId');
        if (!storedTxId) {
          throw new Error('유효하지 않은 인증 요청입니다.');
        }

        // 백엔드에서 결과 조회 및 복호화
        const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.slimrx.co.kr';
        const response = await axiosClient.post<MobileOKResult>(
          `${API_BASE}/func/mobileok/result`,
          {
            mokToken,
            txId: storedTxId,
            userId: options?.userId,
          },
        );

        // 세션 정리
        sessionStorage.removeItem('mobileok_txId');

        setResult(response.data);
        setIsLoading(false);
        options?.onSuccess?.(response.data);
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('본인확인 결과 처리 실패');
        setError(error);
        setIsLoading(false);
        options?.onError?.(error);
      }
    };

    processCallback();
  }, []);

  return { result, isLoading, error };
}

export default useMobileOK;